import React from 'react';
import { createRoot } from 'react-dom/client';
import { PdfPieChart } from '@/components/pdf/PdfPieChart';
import { PdfBarChart } from '@/components/pdf/PdfBarChart';
import { Footer } from '@/components/layout/Footer';
import { captureChartAsImage, ChartCaptureResult } from './chartCaptureService';
import { PdfPhaseData, PdfMaterialData } from '@/types/pdf/pdf.types';

/**
 * Color palettes for charts
 */
export const CHART_COLORS = {
  // Phase colors (for pie charts)
  phases: {
    extraction: '#14b8a6', // teal-500
    transport: '#f59e0b', // amber-500
    transformation: '#8b5cf6', // violet-500
    materials: '#14b8a6', // teal-500
    ecr: '#0891b2', // cyan-600
    blue: '#3b82f6', // blue-500
    grey: '#6b7280', // gray-500
    energy: '#f97316', // orange-500
  },
  // Material colors (for bar charts)
  materials: [
    '#14b8a6', // teal-500
    '#0891b2', // cyan-600
    '#f59e0b', // amber-500
    '#8b5cf6', // violet-500
    '#ef4444', // red-500
    '#10b981', // emerald-500
    '#f97316', // orange-500
    '#6366f1', // indigo-500
  ],
};

/**
 * Delay execution for specified milliseconds
 */
const delay = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Render a React component off-screen and capture it as an image
 */
async function renderAndCaptureComponent(
  component: React.ReactElement,
  chartId: string
): Promise<ChartCaptureResult> {
  // Create temporary container
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '-9999px';
  container.style.left = '-9999px';
  container.style.zIndex = '-1';
  document.body.appendChild(container);

  try {
    // Render the component
    const root = createRoot(container);
    root.render(component);

    // Wait for rendering to complete
    await delay(800);

    // Find the rendered chart element
    const chartElement = container.querySelector(`[data-chart-id="${chartId}"]`) as HTMLElement;
    if (!chartElement) {
      throw new Error(`Chart element with ID ${chartId} not found`);
    }

    // Capture the chart
    const result = await captureChartAsImage(chartElement, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      retryAttempts: 1,
    });

    // Cleanup
    root.unmount();
    document.body.removeChild(container);

    return result;
  } catch (error) {
    // Cleanup on error
    if (container.parentNode) {
      document.body.removeChild(container);
    }

    return {
      dataUrl: '',
      width: 0,
      height: 0,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error rendering chart',
    };
  }
}

/**
 * Assign colors to phase data based on phase names
 */
function assignPhaseColors(phases: PdfPhaseData[]): Array<PdfPhaseData & { color: string }> {
  return phases.map(phase => {
    const nameLower = phase.name.toLowerCase();
    let color = CHART_COLORS.phases.extraction; // default

    // Match phase names to colors
    if (nameLower.includes('material') || nameLower.includes('materi')) {
      color = CHART_COLORS.phases.materials;
    } else if (nameLower.includes('energ') || nameLower.includes('ecr') || nameLower.includes('resídu') || nameLower.includes('residu')) {
      color = CHART_COLORS.phases.ecr;
    } else if (nameLower.includes('blue') || nameLower.includes('azul')) {
      color = CHART_COLORS.phases.blue;
    } else if (nameLower.includes('grey') || nameLower.includes('gray') || nameLower.includes('cinz')) {
      color = CHART_COLORS.phases.grey;
    } else if (nameLower.includes('trans') && nameLower.includes('form')) {
      color = CHART_COLORS.phases.transformation;
    } else if (nameLower.includes('trans') || nameLower.includes('port')) {
      color = CHART_COLORS.phases.transport;
    } else if (nameLower.includes('extra') || nameLower.includes('extract')) {
      color = CHART_COLORS.phases.extraction;
    }

    return {
      ...phase,
      color,
    };
  });
}

/**
 * Assign colors to material data
 */
function assignMaterialColors(materials: PdfMaterialData[]): Array<PdfMaterialData & { color: string }> {
  return materials.map((material, index) => ({
    ...material,
    color: CHART_COLORS.materials[index % CHART_COLORS.materials.length],
  }));
}

/**
 * Render and capture a pie chart for phases
 */
export async function capturePhasePieChart(
  phases: PdfPhaseData[],
  chartId: string
): Promise<ChartCaptureResult> {
  if (!phases || phases.length === 0) {
    return {
      dataUrl: '',
      width: 0,
      height: 0,
      success: false,
      error: 'No phase data provided',
    };
  }

  const phasesWithColors = assignPhaseColors(phases);

  const component = (
    <PdfPieChart
      data={phasesWithColors}
      width={700}
      height={500}
      chartId={chartId}
    />
  );

  return renderAndCaptureComponent(component, chartId);
}

/**
 * Render and capture a bar chart for materials
 */
export async function captureMaterialBarChart(
  materials: PdfMaterialData[],
  unit: string,
  chartId: string,
  yAxisLabel?: string
): Promise<ChartCaptureResult> {
  if (!materials || materials.length === 0) {
    return {
      dataUrl: '',
      width: 0,
      height: 0,
      success: false,
      error: 'No material data provided',
    };
  }

  const materialsWithColors = assignMaterialColors(materials);

  // Sort materials by footprint value descending
  const sortedMaterials = [...materialsWithColors].sort((a, b) => b.footprint - a.footprint);

  // Transform to chart data format
  const chartData = sortedMaterials.map(m => ({
    name: m.name,
    value: m.footprint,
    color: m.color,
  }));

  const component = (
    <PdfBarChart
      data={chartData}
      unit={unit}
      width={800}
      height={480}
      chartId={chartId}
      yAxisLabel={yAxisLabel}
    />
  );

  return renderAndCaptureComponent(component, chartId);
}

/**
 * Render and capture the Footer component
 */
export async function captureFooter(): Promise<ChartCaptureResult> {
  const footerId = 'pdf-footer';
  
  // Create temporary container
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '-9999px';
  container.style.left = '-9999px';
  container.style.zIndex = '-1';
  container.style.width = '800px'; // Set a reasonable width for footer
  document.body.appendChild(container);

  try {
    // Render the Footer component with a data attribute
    const root = createRoot(container);
    root.render(
      <div data-footer-id={footerId}>
        <Footer />
      </div>
    );

    // Wait for rendering and images to load
    await delay(1000);

    // Find the rendered footer element
    const footerElement = container.querySelector(`[data-footer-id="${footerId}"]`) as HTMLElement;
    if (!footerElement) {
      throw new Error('Footer element not found');
    }

    // Capture the footer
    const result = await captureChartAsImage(footerElement, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      retryAttempts: 2,
    });

    // Cleanup
    root.unmount();
    document.body.removeChild(container);

    return result;
  } catch (error) {
    // Cleanup on error
    if (container.parentNode) {
      document.body.removeChild(container);
    }

    return {
      dataUrl: '',
      width: 0,
      height: 0,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error rendering footer',
    };
  }
}

/**
 * PDF Chart Renderer service
 */
export const pdfChartRenderer = {
  capturePhasePieChart,
  captureMaterialBarChart,
  captureFooter,
  assignPhaseColors,
  assignMaterialColors,
};
